/**
 * NewForm design-system Eyebrow.
 *
 * Small kicker / category label that sits above a page title — purple dot
 * + mono uppercase text with wide tracking. Use on detail pages, dashboards,
 * and editorial moments to signal the section/category before the headline.
 *
 * Two API shapes:
 *
 *   // Static label
 *   <Eyebrow>Creative Testing</Eyebrow>
 *
 *   // Breadcrumb segments (any segment with `href` becomes a link)
 *   <Eyebrow
 *     segments={[
 *       { label: "Creative Testing", href: "/creative-testing" },
 *       { label: "Batch Details" },
 *     ]}
 *   />
 *
 * The component inserts a middle-dot separator (` · `) between segments.
 */

import Link from "next/link";
import { Fragment, type ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface EyebrowSegment {
  label: string;
  href?: string;
}

interface EyebrowProps {
  children?: ReactNode;
  segments?: EyebrowSegment[];
  className?: string;
  showDot?: boolean;
}

export function Eyebrow({
  children,
  segments,
  className,
  showDot = true,
}: EyebrowProps) {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      {showDot ? (
        <span className="size-2 rounded-full bg-nf-ds-purple-product" />
      ) : null}
      <span className="font-nf-ds-mono text-[11px] font-normal uppercase tracking-[0.14em] text-muted-foreground/80">
        {segments
          ? segments.map((seg, i) => (
              <Fragment key={`${seg.label}-${i}`}>
                {i > 0 ? (
                  <span aria-hidden="true" className="mx-1.5">
                    ·
                  </span>
                ) : null}
                {seg.href ? (
                  <Link
                    href={seg.href}
                    className="font-nf-ds-mono hover:text-foreground transition-colors"
                  >
                    {seg.label}
                  </Link>
                ) : (
                  seg.label
                )}
              </Fragment>
            ))
          : children}
      </span>
    </div>
  );
}
